/**
 * Últimos acessos do usuário (JOR4-7663, RN10).
 *
 * O registro é feito no `ControleTabPadrao`, por onde passa toda abertura de
 * aba — menu, acesso rápido e os CTAs das seções. Ficar aqui, e não na tela de
 * Início, é o que permite isso: `componentes-padrao` não pode depender de uma
 * tela.
 *
 * A lista é persistida em `/user-screen-preferences`, na mesma infraestrutura
 * da personalização da Início mas em linha própria — dado volátil (muda a cada
 * clique) separado de configuração (muda quando o usuário decide). A gravação
 * é adiada e nunca propaga erro: abrir uma tela não pode falhar por causa do
 * histórico.
 */

import {
    getScreenPreferences,
    putScreenPreferences,
} from "../services/user-screen-preferences/api";

/** Linha própria, para o histórico não disputar espaço com a configuração. */
export const TELA_ACESSOS_RECENTES = "inicio-acessos-recentes";

/** RN10: o dropdown mostra 5 acessos, cada tela uma única vez. */
export const MAXIMO_ACESSOS_RECENTES = 5;

/**
 * Quantos acessos ficam guardados. Maior do que os 5 exibidos de propósito.
 *
 * Nem toda aba aberta é uma tela que o usuário pode alcançar de volta pelo
 * menu: `TelaVisaoBIJornada`, `ManutencaoJornadaTerceiros` e
 * `GestaoViagensJornadaTerceiros` são abertas com contexto por outras telas e
 * não têm sequer cadastro em `jtj_tela`, então nunca aparecem no dropdown
 * (quem decide isso é o catálogo, no `UltimosAcessos`). Cortando em 5 já na
 * gravação, três dessas abas empurrariam para fora do histórico as telas de
 * verdade e o usuário veria uma lista quase vazia sem entender por quê.
 *
 * O teto continua existindo porque isto é dado volátil dentro de uma linha de
 * preferência de 64 KB — não é para virar um log de navegação.
 */
export const LIMITE_HISTORICO = 20;

/**
 * Espera antes de gravar. Agrupa a rajada de quem navega por várias telas
 * seguidas; em troca, fechar o navegador logo depois de abrir uma tela perde
 * aquele registro — barato perto de um PUT por clique de menu.
 */
const ATRASO_GRAVACAO = 2000;

export interface AcessoRecente {
    /** TLA_CLASSEGRID da tela aberta. */
    classInvoke: string;
    titulo: string;
    /** Momento do acesso, em ISO 8601. */
    em: string;
}

/** Formato gravado. Objeto, e não array, para caber campo novo depois. */
interface AcessosGuardados {
    acessos?: AcessoRecente[];
}

const MINUTO = 60 * 1000;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

/**
 * "há 42 min", "há 3 h" — o texto do protótipo.
 *
 * Função própria em vez de `moment.fromNow()`, que arredonda para linguagem
 * natural ("há uma hora") e não bate com o protótipo. Data inválida devolve
 * texto vazio: melhor um item sem tempo do que "há NaN min".
 */
export const tempoDesde = (em: string, agora: Date = new Date()): string => {
    const quando = new Date(em).getTime();
    if (Number.isNaN(quando)) return "";

    // Relógio do cliente adiantado em relação ao registro deixaria a diferença
    // negativa; para o usuário isso é "agora".
    const decorrido = agora.getTime() - quando;

    if (decorrido < MINUTO) return "agora";
    if (decorrido < HORA) return `há ${Math.floor(decorrido / MINUTO)} min`;
    if (decorrido < DIA) return `há ${Math.floor(decorrido / HORA)} h`;

    return `há ${Math.floor(decorrido / DIA)} d`;
};

const valido = (acesso: AcessoRecente | undefined | null): boolean =>
    Boolean(
        acesso &&
            acesso.classInvoke &&
            acesso.em &&
            !Number.isNaN(new Date(acesso.em).getTime()),
    );

/**
 * Mais recentes primeiro, uma linha por tela, até o teto do histórico. O corte
 * em 5 é do dropdown, depois do filtro de permissão. (RN10)
 */
const consolidar = (acessos: AcessoRecente[]): AcessoRecente[] => {
    const porTela = new Map<string, AcessoRecente>();

    for (const acesso of acessos) {
        if (!valido(acesso)) continue;

        const atual = porTela.get(acesso.classInvoke);
        if (!atual || new Date(acesso.em) > new Date(atual.em)) {
            porTela.set(acesso.classInvoke, acesso);
        }
    }

    return Array.from(porTela.values())
        .sort((a, b) => new Date(b.em).getTime() - new Date(a.em).getTime())
        .slice(0, LIMITE_HISTORICO);
};

let acessos: AcessoRecente[] = [];
let carregado = false;
let gravacaoAgendada: ReturnType<typeof setTimeout> | null = null;

const gravar = async (): Promise<void> => {
    try {
        /*
         * Ler antes de gravar, sempre. O PUT substitui a linha inteira, e a
         * carga só acontecia quando o usuário abria o dropdown — quem abrisse
         * uma tela e nunca o abrisse gravava a lista desta sessão por cima do
         * histórico das anteriores, reduzindo os "últimos 5" ao que coubesse
         * numa sessão. A chamada é idempotente: da segunda vez em diante
         * responde da memória, sem ir ao servidor.
         */
        await carregarAcessosRecentes();

        // Depois do await: a carga funde o que veio do banco com o que foi
        // registrado nesta sessão, e é esse resultado que tem de ser gravado.
        await putScreenPreferences<AcessosGuardados>(TELA_ACESSOS_RECENTES, {
            acessos,
        });
    } catch (error) {
        // Sem notify: o usuário não pediu para gravar nada, ele só abriu uma
        // tela. O histórico volta a ser gravado no próximo acesso.
        console.warn("Não foi possível gravar os últimos acessos.", error);
    }
};

const agendarGravacao = (): void => {
    if (gravacaoAgendada) clearTimeout(gravacaoAgendada);

    gravacaoAgendada = setTimeout(() => {
        gravacaoAgendada = null;
        // `gravar` trata o próprio erro; nada aqui espera por ele.
        void gravar();
    }, ATRASO_GRAVACAO);
};

/**
 * Registra a abertura de uma tela. Chamado pelo ControleTabPadrao a cada aba
 * aberta que não seja a aba fixa — a Início não é um "acesso".
 */
export const registrarAcessoRecente = (
    titulo: string,
    classInvoke: string,
    agora: Date = new Date(),
): void => {
    if (!classInvoke) return;

    acessos = consolidar([
        { classInvoke, titulo, em: agora.toISOString() },
        ...acessos,
    ]);

    agendarGravacao();
};

/**
 * Lista para a tela de Início. Busca o que está gravado na primeira chamada e
 * mantém em memória daí em diante — a aba de Início fica montada a sessão
 * inteira e relê a lista a cada abertura do dropdown.
 *
 * O que já foi registrado nesta sessão tem precedência sobre o que veio do
 * banco: a carga pode terminar depois das primeiras navegações.
 */
export const carregarAcessosRecentes = async (): Promise<AcessoRecente[]> => {
    if (carregado) return acessos;

    try {
        const guardado =
            await getScreenPreferences<AcessosGuardados>(TELA_ACESSOS_RECENTES);

        acessos = consolidar(acessos.concat(guardado?.acessos ?? []));
    } catch (error) {
        console.warn("Não foi possível carregar os últimos acessos.", error);
    }

    carregado = true;

    return acessos;
};

/** Lista em memória, sem ir ao servidor. */
export const acessosRecentesAtuais = (): AcessoRecente[] => acessos;

/**
 * Dispara agora o que estiver pendente, em vez de esperar o atraso. Devolve a
 * promessa da gravação: na primeira vez ela ainda passa pela leitura, então
 * "agora" é o início da gravação, não o fim dela.
 */
export const sincronizarAcessosRecentes = (): Promise<void> => {
    if (!gravacaoAgendada) return Promise.resolve();

    clearTimeout(gravacaoAgendada);
    gravacaoAgendada = null;

    return gravar();
};

/** Só para os testes: devolve o módulo ao estado de sessão nova. */
export const redefinirAcessosRecentes = (): void => {
    acessos = [];
    carregado = false;

    if (gravacaoAgendada) clearTimeout(gravacaoAgendada);
    gravacaoAgendada = null;
};
